import { Navigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { buildLegacySeriesUrlFromMediaItem, mediaURL } from '@jwp/ott-common/src/utils/urlFormatting';
import { getLegacySeriesPlaylistId } from '@jwp/ott-common/src/utils/media';
import { useSeriesLookup } from '@jwp/ott-hooks-react/src/series/useSeriesLookup';
import useMedia from '@jwp/ott-hooks-react/src/useMedia';

import type { ScreenComponent } from '../../../../../types/screens';
import Loading from '../../../Loading/Loading';
import MediaMovie from '../MediaMovie/MediaMovie';

/**
 * This media series episode screen is used to redirect an episode item to the series page.
 */
const MediaEpisode: ScreenComponent<PlaylistItem> = ({ data: media, isLoading: isMediaLoading }) => {
  const mediaId = media.mediaid;
  const [searchParams] = useSearchParams();

  const play = searchParams.get('play') === '1';
  const feedId = searchParams.get('r');

  const { data: episodeInSeries, isLoading: isSeriesIdLoading } = useSeriesLookup(media.mediaid);
  const { isLoading: isSeriesMediaLoading, data: seriesMedia } = useMedia(episodeInSeries?.series_id || '');

  // Prevent rendering multiple times when we are loading data
  if (isMediaLoading || isSeriesIdLoading || isSeriesMediaLoading) {
    return <Loading />;
  }

  if (!episodeInSeries) {
    // Without a series or a legacy series playlist id there is nothing to redirect to, so play the item as a regular video
    if (!getLegacySeriesPlaylistId(media)) {
      return <MediaMovie data={media} isLoading={isMediaLoading} />;
    }

    return <Navigate to={buildLegacySeriesUrlFromMediaItem(media, play, feedId)} replace />;
  }

  const { mediaid: id, title } = seriesMedia as PlaylistItem;

  // Use media episode item for legacy series flow
  return (
    <Navigate
      to={mediaURL({
        episodeId: mediaId,
        id,
        title,
        play,
        playlistId: feedId,
      })}
      replace
    />
  );
};

export default MediaEpisode;
